/**
 * O que a ficha do foco passa para a página da denúncia (`/denuncia`), aberta
 * numa aba nova: o foco vai no endereço (o link reabre a mesma página); os
 * focos vizinhos, que só existem no mapa que estava aberto, vão por um bilhete
 * no `localStorage` (o endereço guarda só a chave dele). Sem o bilhete (link
 * compartilhado, outro aparelho), a página mostra tudo menos os vizinhos.
 */
import { formatCompactUtc, parseCompactUtc } from '../lib/time';
import type { FireDataset } from '../fires/dataset';
import type { Fire, FireSource } from '../fires/types';

export const CAMINHO_DENUNCIA = '/denuncia';

/** Raio em que os outros focos entram como evidência (km). */
export const RAIO_VIZINHOS_KM = 10;
/** No máximo tantos vizinhos no bilhete (os mais próximos). */
export const MAX_VIZINHOS = 300;

const PREFIXO = 'amaview:denuncia:';
/** Bilhetes mais velhos que isso são apagados quando outro é gravado. */
const VALIDADE_MS = 7 * 24 * 3_600_000;

export interface FocoVizinho {
  lat: number;
  lon: number;
  t: number;
  sat: string;
  /** Distância até o foco da denúncia (km). */
  km: number;
}

export interface Bilhete {
  criado: number;
  /** A janela de tempo que o mapa tinha carregada (os vizinhos são dela). */
  janela: { de: number; ate: number } | null;
  vizinhos: FocoVizinho[];
}

export interface Pedido {
  fire: Fire;
  /** Município estimado pela posição (o INPE não informou). */
  municipioEstimado: boolean;
  /** Chave do bilhete no localStorage. */
  bilhete: string | null;
}

/** Distância entre dois pontos (km, esfera de 6371 km). */
export function distanciaKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r;
  const dLon = (lon2 - lon1) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Os outros focos do conjunto a até `raioKm` do ponto, do mais perto ao mais
 * longe (o próprio foco fica de fora). Uma passada pelo conjunto, com a caixa
 * em graus antes da conta da distância.
 */
export function focosVizinhos(ds: FireDataset, fire: Pick<Fire, 'lat' | 'lon' | 't'>, raioKm = RAIO_VIZINHOS_KM, max = MAX_VIZINHOS): FocoVizinho[] {
  const dLat = raioKm / 111;
  const dLon = raioKm / (111 * Math.max(0.1, Math.cos((fire.lat * Math.PI) / 180)));
  const out: FocoVizinho[] = [];
  for (let i = 0; i < ds.n; i++) {
    const tb = ds.tables[ds.tbl[i]!];
    if (!tb) continue;
    const r = ds.row[i]!;
    const lat = tb.lat[r]!;
    const lon = tb.lon[r]!;
    if (Math.abs(lat - fire.lat) > dLat || Math.abs(lon - fire.lon) > dLon) continue;
    const t = tb.t[r]!;
    if (lat === fire.lat && lon === fire.lon && t === fire.t) continue;
    const km = distanciaKm(fire.lat, fire.lon, lat, lon);
    if (km > raioKm) continue;
    out.push({ lat, lon, t, sat: tb.dict.sat[tb.sat[r]!] ?? '?', km });
  }
  out.sort((a, b) => a.km - b.km || a.t - b.t);
  return out.slice(0, max);
}

/** A janela de tempo do conjunto (null vazio). */
export function janelaDoConjunto(ds: FireDataset): Bilhete['janela'] {
  return ds.n ? { de: ds.t[0]!, ate: ds.t[ds.n - 1]! } : null;
}

function num(v: string | null): number | null {
  if (v === null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** O endereço da página para um foco (relativo; `bilhete` opcional). */
export function urlDaDenuncia(fire: Fire, opcoes: { municipioEstimado?: boolean; bilhete?: string | null } = {}): string {
  const p = new URLSearchParams({ lat: fire.lat.toFixed(5), lon: fire.lon.toFixed(5), t: formatCompactUtc(fire.t), sat: fire.satelite, fonte: fire.source });
  const opc: [string, string | number | null | undefined][] = [
    ['id', fire.source === 'diario' ? fire.id : null],
    ['mun', fire.municipio],
    ['uf', fire.estado],
    ['ibge', fire.cdMun],
    ['bio', fire.bioma],
    ['frp', fire.frp],
    ['dsc', fire.diasSemChuva],
    ['pr', fire.precipitacao],
    ['rf', fire.riscoFogo],
  ];
  for (const [k, v] of opc) if (v !== null && v !== undefined && v !== '') p.set(k, String(v));
  if (opcoes.municipioEstimado) p.set('est', '1');
  if (opcoes.bilhete) p.set('b', opcoes.bilhete);
  return `${CAMINHO_DENUNCIA}?${p}`;
}

/** Lê o endereço da página; null quando falta o essencial (ponto e horário). */
export function lerPedido(search: string): Pedido | null {
  const p = new URLSearchParams(search);
  const lat = num(p.get('lat'));
  const lon = num(p.get('lon'));
  const t = parseCompactUtc(p.get('t') ?? '');
  if (lat === null || lon === null || t === null || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  // `fonte` desde 29/10/2026 (o firewall do MPF bloqueia "src=" no relato); os links antigos trazem `src`.
  const src: FireSource = (p.get('fonte') ?? p.get('src')) === 'goes10min' ? 'goes10min' : 'diario';
  const texto = (k: string) => p.get(k)?.trim() || null;
  const fire: Fire = {
    id: texto('id') ?? `${lat}|${lon}|${t}`,
    lat,
    lon,
    t,
    satelite: texto('sat') ?? '?',
    source: src,
    municipio: texto('mun'),
    estado: texto('uf')?.toUpperCase() ?? null,
    cdMun: texto('ibge'),
    bioma: texto('bio'),
    frp: num(p.get('frp')),
    diasSemChuva: num(p.get('dsc')),
    precipitacao: num(p.get('pr')),
    riscoFogo: num(p.get('rf')),
  };
  return { fire, municipioEstimado: p.get('est') === '1', bilhete: texto('b') };
}

function armazenamento(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Grava o bilhete e devolve a chave (null sem armazenamento). Apaga os velhos. */
export function gravarBilhete(b: Omit<Bilhete, 'criado'>, agora = Date.now()): string | null {
  const ls = armazenamento();
  if (!ls) return null;
  try {
    for (let i = ls.length - 1; i >= 0; i--) {
      const k = ls.key(i);
      if (!k?.startsWith(PREFIXO)) continue;
      const criado = Number(JSON.parse(ls.getItem(k) ?? '{}').criado);
      if (!(agora - criado < VALIDADE_MS)) ls.removeItem(k);
    }
    const chave = `${agora.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    ls.setItem(PREFIXO + chave, JSON.stringify({ ...b, criado: agora }));
    return chave;
  } catch {
    return null;
  }
}

export function lerBilhete(chave: string | null): Bilhete | null {
  if (!chave) return null;
  try {
    const b = JSON.parse(armazenamento()?.getItem(PREFIXO + chave) ?? 'null') as Bilhete | null;
    return b && Array.isArray(b.vizinhos) ? b : null;
  } catch {
    return null;
  }
}
